/**
 * Reusable FormField component for use with React Hook Form
 */

import React from 'react'
import { useFormContext } from 'react-hook-form'
import { AlertCircle } from 'lucide-react'
import { cn } from '@/lib/utils'

interface FormFieldOption {
  value: string
  label: string
}

interface FormFieldProps {
  name: string
  label: string
  type?: 'text' | 'email' | 'password' | 'number' | 'textarea' | 'select'
  placeholder?: string
  disabled?: boolean
  required?: boolean
  className?: string
  rightIcon?: React.ReactNode
  onRightIconClick?: () => void
  rightIconTabIndex?: number
  options?: FormFieldOption[] // For select fields
  inputType?: string // For dynamic type switching (e.g., password visibility)
  helperText?: string // Optional helper text below the field
  step?: string
  min?: string
  max?: string
}

export const FormField: React.FC<FormFieldProps> = ({
  name,
  label,
  type = 'text',
  placeholder,
  disabled = false,
  required = false,
  className,
  rightIcon,
  onRightIconClick,
  rightIconTabIndex,
  options = [],
  inputType,
  helperText,
  step,
  min,
  max,
}) => {
  const {
    register,
    formState: { errors, touchedFields }
  } = useFormContext()

  const error = errors[name]
  const touched = touchedFields[name]
  const hasError = touched && error

  const inputId = `${name}-field`

  return (
    <div className={cn('space-y-2', className)}>
      <label htmlFor={inputId} className="block text-sm font-medium text-foreground">
        {label}
        {required && <span className="text-destructive ml-1">*</span>}
      </label>
      
      <div className="relative">
        {type === 'select' ? (
          <select
            id={inputId}
            disabled={disabled}
            {...register(name)}
            className={cn(
              'w-full px-3 py-2 border rounded-md shadow-sm bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary transition-colors',
              hasError
                ? 'border-destructive focus:border-destructive focus:ring-destructive'
                : 'border-border',
              disabled && 'opacity-50 cursor-not-allowed'
            )}
          >
            {options.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        ) : type === 'textarea' ? (
          <textarea
            id={inputId}
            placeholder={placeholder}
            disabled={disabled}
            {...register(name)}
            className={cn(
              'w-full px-3 py-2 border rounded-md shadow-sm bg-background text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary transition-colors resize-vertical',
              hasError
                ? 'border-destructive focus:border-destructive focus:ring-destructive'
                : 'border-border',
              disabled && 'opacity-50 cursor-not-allowed',
              className
            )}
            rows={4}
          />
        ) : (
          <input
            id={inputId}
            type={inputType || type}
            placeholder={placeholder}
            disabled={disabled}
            step={step}
            min={min}
            max={max}
            {...register(name, { valueAsNumber: type === 'number' })}
            className={cn(
              'w-full px-3 py-2 border rounded-md shadow-sm bg-background text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary transition-colors',
              hasError
                ? 'border-destructive focus:border-destructive focus:ring-destructive'
                : 'border-border',
              rightIcon && 'pr-10',
              disabled && 'opacity-50 cursor-not-allowed'
            )}
          />
        )}
        
        {/* Right icon (like password visibility toggle) */}
        {rightIcon && type !== 'select' && type !== 'textarea' && (
          <button
            type="button"
            onClick={onRightIconClick}
            disabled={disabled}
            tabIndex={rightIconTabIndex}
            className="absolute inset-y-0 right-0 pr-3 flex items-center text-muted-foreground hover:text-foreground transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {rightIcon}
          </button>
        )}
        
        {/* Error icon */}
        {hasError && !rightIcon && (
          <AlertCircle className="absolute right-3 top-2.5 h-4 w-4 text-destructive" />
        )}
        
        {/* Error icon when there's also a right icon */}
        {hasError && rightIcon && type !== 'select' && type !== 'textarea' && (
          <AlertCircle className="absolute right-8 top-2.5 h-4 w-4 text-destructive" />
        )}
      </div>
      
      {hasError && (
        <div className="text-sm text-destructive">
          {error?.message as string}
        </div>
      )}

      {helperText && (
        <div className="text-sm text-muted-foreground">
          {helperText}
        </div>
      )}
    </div>
  )
}

export default FormField 